import { useEffect, useMemo, useRef, useState } from "react";
import { api, isFolderNotEmptyError } from "../lib/api";
import { collectDroppedUploadFiles, resolveFolderUploadItems, type FolderRecord } from "../lib/folderUpload";
import {
  docKey,
  folderContainsTarget,
  folderKey,
  foldersByDepth,
  parseSelectKey,
  selectAllKeys,
  toggleKey,
  type SelectKey,
} from "../lib/fileSelection";
import { isOfficeFile } from "../lib/fileTypes";
import { downloadDocument } from "../lib/fetchDocument";
import { useUploadQueue } from "./UploadQueue";
import { EmptyState } from "./EmptyState";
import { Modal } from "./Modal";
import { FileRowCheckbox } from "./FileRowCheckbox";
import {
  FileTypeIcon,
  IconChevronUp,
  IconDownload,
  IconFolder,
  IconMove,
  IconSearch,
  IconTrash,
  IconUpload,
} from "./Icons";

const UPLOAD_ACCEPT =
  ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.png,.jpg,.jpeg,.webp,.gif";

export type Folder = { id: string; name: string; parent_id: string | null; path: string; stage_gate?: string };
export type Doc = {
  id: string;
  filename: string;
  display_name?: string;
  folder_id: string | null;
  status: string;
  page_count?: number;
  mime_type?: string;
};

type Props = {
  roomId: string;
  folders: Folder[];
  documents: Doc[];
  folderId: string | null;
  onFolderChange: (id: string | null) => void;
  onReload: () => Promise<void>;
};

type Dialog =
  | { kind: "folder-create" }
  | { kind: "folder-rename"; folder: Folder }
  | { kind: "doc-rename"; doc: Doc }
  | { kind: "doc-move"; doc: Doc }
  | { kind: "confirm-delete-folder"; folder: Folder; force?: boolean }
  | { kind: "confirm-delete-doc"; doc: Doc }
  | { kind: "bulk-move"; docIds: string[]; folderIds: string[] }
  | { kind: "bulk-delete"; docIds: string[]; folderIds: string[]; force?: boolean }
  | null;

function statusLabel(status: string, filename: string, mimeType?: string) {
  if (status === "ready") return "Ready";
  if (status === "processing" || status === "uploading") {
    return isOfficeFile(filename, mimeType) ? "Converting to PDF…" : "Processing…";
  }
  if (status === "failed") return "Conversion failed";
  return status;
}

export function FileBrowser({ roomId, folders, documents, folderId, onFolderChange, onReload }: Props) {
  const { enqueue, jobs } = useUploadQueue();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const uploadMenuRef = useRef<HTMLDivElement>(null);
  const busyRef = useRef(false);
  const searchReqRef = useRef(0);
  const [dragOver, setDragOver] = useState(false);
  const [uploadMenuOpen, setUploadMenuOpen] = useState(false);
  const [uploadNotice, setUploadNotice] = useState<string | null>(null);
  const [menuId, setMenuId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [dialog, setDialog] = useState<Dialog>(null);
  const [draft, setDraft] = useState("");
  const [moveError, setMoveError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [remoteHits, setRemoteHits] = useState<Array<{ documentId: string; title: string; folderPath?: string | null; snippet?: string; matchType?: string }> | null>(null);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<Set<SelectKey>>(() => new Set());
  const selectAllRef = useRef<HTMLInputElement>(null);

  const currentFolder = folders.find((f) => f.id === folderId) ?? null;
  const folderLabel = currentFolder?.path?.replace(/^\//, "") || "Space root";

  const openAdminPreview = (doc: Doc) => {
    window.open(`/rooms/${roomId}/preview/${doc.id}`, "_blank", "noopener,noreferrer");
  };

  const childFolders = useMemo(
    () =>
      folders
        .filter((f) => f.parent_id === folderId)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [folders, folderId],
  );

  const docsHere = useMemo(() => {
    const base = documents.filter((d) => d.folder_id === folderId);
    const q = query.trim().toLowerCase();
    if (!q) return base;
    return base.filter((d) => (d.display_name || d.filename).toLowerCase().includes(q));
  }, [documents, folderId, query]);

  const selectableKeys = useMemo(() => {
    if (query.trim().length >= 2) return [] as SelectKey[];
    const keys: SelectKey[] = childFolders.map((f) => folderKey(f.id));
    for (const d of docsHere) keys.push(docKey(d.id));
    return keys;
  }, [childFolders, docsHere, query]);

  const selectionCount = selected.size;
  const selectedDocs = useMemo(
    () =>
      [...selected]
        .map(parseSelectKey)
        .filter((x) => x.kind === "doc")
        .map((x) => documents.find((d) => d.id === x.id))
        .filter(Boolean) as Doc[],
    [selected, documents],
  );
  const selectedFolders = useMemo(
    () =>
      [...selected]
        .map(parseSelectKey)
        .filter((x) => x.kind === "folder")
        .map((x) => folders.find((f) => f.id === x.id))
        .filter(Boolean) as Folder[],
    [selected, folders],
  );
  const allSelected = selectableKeys.length > 0 && selectableKeys.every((k) => selected.has(k));
  const someSelected = selectableKeys.some((k) => selected.has(k));
  const readyDownloadCount = selectedDocs.filter((d) => d.status === "ready").length;

  useEffect(() => {
    setSelected(new Set());
  }, [folderId]);

  useEffect(() => {
    if (query.trim().length >= 2) setSelected(new Set());
  }, [query]);

  useEffect(() => {
    const el = selectAllRef.current;
    if (!el) return;
    el.indeterminate = someSelected && !allSelected;
  }, [someSelected, allSelected]);

  useEffect(() => {
    if (!selectionCount) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !dialog) setSelected(new Set());
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selectionCount, dialog]);

  useEffect(() => {
    const q = query.trim();
    const forRoom = roomId;
    const req = ++searchReqRef.current;
    if (q.length < 2) {
      setRemoteHits(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    const t = window.setTimeout(() => {
      api<{ results: Array<{ documentId: string; title: string; folderPath?: string | null; snippet?: string; matchType?: string }> }>(
        `/api/v1/search/rooms/${forRoom}?q=${encodeURIComponent(q)}`,
      )
        .then((res) => {
          if (req !== searchReqRef.current) return;
          setRemoteHits(res.results ?? []);
        })
        .catch(() => {
          if (req !== searchReqRef.current) return;
          setRemoteHits([]);
        })
        .finally(() => {
          if (req === searchReqRef.current) setSearching(false);
        });
    }, 280);
    return () => window.clearTimeout(t);
  }, [query, roomId]);

  const crossFolderHits = useMemo(() => {
    if (!remoteHits || !query.trim()) return null;
    return remoteHits
      .map((hit) => {
        const doc = documents.find((d) => d.id === hit.documentId);
        if (!doc) return null;
        return { doc, hit };
      })
      .filter(Boolean) as Array<{ doc: Doc; hit: { title: string; folderPath?: string | null; snippet?: string; matchType?: string } }>;
  }, [remoteHits, documents, query]);

  const breadcrumbs = useMemo(() => {
    const crumbs: Array<{ id: string | null; label: string }> = [{ id: null, label: "Space" }];
    if (!folderId) return crumbs;
    const folder = folders.find((f) => f.id === folderId);
    if (!folder) return crumbs;
    const parts = folder.path.split("/").filter(Boolean);
    let walk: string | null = null;
    let acc = "";
    for (const part of parts) {
      acc += `/${part}`;
      const match = folders.find((f) => f.path === acc);
      walk = match?.id ?? walk;
      crumbs.push({ id: walk, label: part });
    }
    return crumbs;
  }, [folderId, folders]);

  const uploadingHere = jobs.filter(
    (j) => j.roomId === roomId && j.folderId === folderId && (j.status === "queued" || j.status === "uploading" || j.status === "processing"),
  );

  useEffect(() => {
    const el = folderInputRef.current;
    if (!el) return;
    el.setAttribute("webkitdirectory", "");
    el.setAttribute("directory", "");
  }, []);

  useEffect(() => {
    if (!menuId) return;
    const onOutsideClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (target?.closest?.(`[data-file-menu="${menuId}"]`)) return;
      setMenuId(null);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !dialog) setMenuId(null);
    };
    const timer = window.setTimeout(() => document.addEventListener("click", onOutsideClick), 0);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("click", onOutsideClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuId, dialog]);

  useEffect(() => {
    if (!uploadMenuOpen) return;
    const onOutside = (e: MouseEvent) => {
      if (uploadMenuRef.current?.contains(e.target as Node)) return;
      setUploadMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setUploadMenuOpen(false);
    };
    const timer = window.setTimeout(() => document.addEventListener("click", onOutside), 0);
    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("click", onOutside);
      document.removeEventListener("keydown", onKey);
    };
  }, [uploadMenuOpen]);

  const startUpload = (files: FileList | File[] | null) => {
    if (!files?.length) return;
    setUploadNotice(null);
    enqueue({ roomId, folderId, folderLabel, files, onComplete: () => onReload().catch(console.error) });
  };

  const startFolderUpload = async (files: FileList | File[] | null) => {
    if (!files?.length) return;
    setBusy(true);
    setUploadNotice(null);
    try {
      const { items } = await resolveFolderUploadItems(
        roomId,
        folderId,
        folderLabel,
        folders as FolderRecord[],
        [...files],
      );
      await onReload();
      enqueue({
        roomId,
        items,
        onComplete: () => onReload().catch(console.error),
      });
      const folderCount = new Set(items.map((i) => i.folderId)).size;
      setUploadNotice(
        `Uploading ${items.length} file${items.length === 1 ? "" : "s"} into ${folderCount} folder${folderCount === 1 ? "" : "s"}.`,
      );
    } catch (e) {
      const msg = String((e as Error).message || e);
      setUploadNotice(
        msg === "no_supported_files"
          ? "No supported files found in that folder."
          : "Could not upload folder. Try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = await collectDroppedUploadFiles(e.dataTransfer);
    if (!files.length) return;
    const structured = files.some((f) => relativePath(f).includes("/"));
    if (structured) {
      await startFolderUpload(files);
      return;
    }
    startUpload(files);
  };

  function relativePath(file: File): string {
    return (file as File & { webkitRelativePath?: string }).webkitRelativePath?.trim() || file.name;
  }

  const openDialog = (next: Dialog) => {
    setMenuId(null);
    setActionError(null);
    setDialog(next);
  };

  const openCreateFolder = () => {
    setDraft("");
    openDialog({ kind: "folder-create" });
  };

  const onBrowserKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const target = e.target as HTMLElement | null;
    if (target?.matches("input, textarea, select, [contenteditable=true]")) return;
    const rows = Array.from(e.currentTarget.querySelectorAll<HTMLElement>(".file-row"));
    if (!rows.length) return;
    const active = document.activeElement as HTMLElement | null;
    const idx = rows.findIndex((r) => r === active || (active != null && r.contains(active)));
    let next = idx;
    if (e.key === "ArrowDown") next = idx < 0 ? 0 : Math.min(idx + 1, rows.length - 1);
    else next = idx < 0 ? rows.length - 1 : Math.max(idx - 1, 0);
    if (next === idx && idx >= 0) return;
    e.preventDefault();
    rows[next]?.focus();
  };

  const openBulkMove = () => {
    const docIds = selectedDocs.map((d) => d.id);
    const folderIds = selectedFolders.map((f) => f.id);
    if (!docIds.length && !folderIds.length) return;
    setDraft("");
    setMoveError(null);
    openDialog({ kind: "bulk-move", docIds, folderIds });
  };

  const openBulkDelete = () => {
    const docIds = selectedDocs.map((d) => d.id);
    const folderIds = selectedFolders.map((f) => f.id);
    if (!docIds.length && !folderIds.length) return;
    openDialog({ kind: "bulk-delete", docIds, folderIds });
  };

  const bulkDownload = async () => {
    const ready = selectedDocs.filter((d) => d.status === "ready");
    if (!ready.length) return;
    setBusy(true);
    try {
      for (const doc of ready) {
        await downloadDocument(`/api/v1/files/${doc.id}/download`, doc.filename, { admin: true });
        await new Promise((r) => window.setTimeout(r, 280));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const submitDialog = async () => {
    if (!dialog || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      if (dialog.kind === "folder-create") {
        if (!draft.trim()) return;
        await api(`/api/v1/rooms/${roomId}/folders`, {
          method: "POST",
          body: JSON.stringify({ name: draft.trim(), parentId: folderId }),
        });
      } else if (dialog.kind === "folder-rename") {
        if (!draft.trim() || draft.trim() === dialog.folder.name) return;
        await api(`/api/v1/rooms/${roomId}/folders/${dialog.folder.id}`, {
          method: "PATCH",
          body: JSON.stringify({ name: draft.trim() }),
        });
      } else if (dialog.kind === "doc-rename") {
        if (!draft.trim()) return;
        await api(`/api/v1/files/${dialog.doc.id}`, {
          method: "PATCH",
          body: JSON.stringify({ displayName: draft.trim() }),
        });
      } else if (dialog.kind === "doc-move") {
        const pick = draft.trim();
        const target =
          !pick || pick === "(Space root)"
            ? null
            : folders.find((f) => f.path.replace(/^\//, "") === pick || f.path === pick || f.id === pick)?.id ?? null;
        if (pick && pick !== "(Space root)" && target === null) {
          setMoveError("Folder not found. Choose an exact path.");
          return;
        }
        setMoveError(null);
        await api(`/api/v1/files/${dialog.doc.id}`, {
          method: "PATCH",
          body: JSON.stringify(target === null ? { clearFolder: true } : { folderId: target }),
        });
      } else if (dialog.kind === "confirm-delete-folder") {
        const f = dialog.folder;
        try {
          await api(`/api/v1/rooms/${roomId}/folders/${f.id}${dialog.force ? "?force=1" : ""}`, {
            method: "DELETE",
          });
        } catch (e) {
          if (!dialog.force && isFolderNotEmptyError(e)) {
            setDialog({ kind: "confirm-delete-folder", folder: f, force: true });
            return;
          }
          throw e;
        }
        if (folderId === f.id) onFolderChange(f.parent_id);
      } else if (dialog.kind === "confirm-delete-doc") {
        await api(`/api/v1/files/${dialog.doc.id}`, { method: "DELETE" });
        setMenuId(null);
      } else if (dialog.kind === "bulk-move") {
        const pick = draft.trim();
        const target =
          !pick || pick === "(Space root)"
            ? null
            : folders.find((f) => f.path.replace(/^\//, "") === pick || f.path === pick || f.id === pick)?.id ?? null;
        if (pick && pick !== "(Space root)" && target === null) {
          setMoveError("Folder not found. Choose an exact path.");
          return;
        }
        for (const moving of foldersByDepth(
          dialog.folderIds.map((id) => folders.find((f) => f.id === id)).filter(Boolean) as Folder[],
        )) {
          if (target && dialog.folderIds.includes(target)) {
            setMoveError("Pick a destination outside the selected folders.");
            return;
          }
          if (target && (moving.id === target || folderContainsTarget(moving, target, folders))) {
            setMoveError(`Cannot move “${moving.name}” into that folder.`);
            return;
          }
        }
        setMoveError(null);
        for (const docId of dialog.docIds) {
          await api(`/api/v1/files/${docId}`, {
            method: "PATCH",
            body: JSON.stringify(target === null ? { clearFolder: true } : { folderId: target }),
          });
        }
        for (const moving of foldersByDepth(
          dialog.folderIds.map((id) => folders.find((f) => f.id === id)).filter(Boolean) as Folder[],
        )) {
          if (target === moving.id) continue;
          await api(`/api/v1/rooms/${roomId}/folders/${moving.id}`, {
            method: "PATCH",
            body: JSON.stringify({ parentId: target }),
          });
        }
        setSelected(new Set());
      } else if (dialog.kind === "bulk-delete") {
        // Force retry must not re-DELETE docs already removed in the first pass.
        if (!dialog.force) {
          for (const docId of dialog.docIds) {
            await api(`/api/v1/files/${docId}`, { method: "DELETE" });
          }
        }
        const folderRows = foldersByDepth(
          dialog.folderIds.map((id) => folders.find((f) => f.id === id)).filter(Boolean) as Folder[],
        );
        let needsForce = false;
        for (const f of folderRows) {
          try {
            await api(`/api/v1/rooms/${roomId}/folders/${f.id}${dialog.force ? "?force=1" : ""}`, {
              method: "DELETE",
            });
          } catch (e) {
            if (!dialog.force && isFolderNotEmptyError(e)) {
              needsForce = true;
              break;
            }
            const msg = String((e as Error).message || e);
            // Already gone (prior pass or concurrent delete): treat as success.
            if (msg.includes("not_found") || msg.includes("404")) {
              continue;
            }
            throw e;
          }
          if (folderId === f.id) onFolderChange(f.parent_id);
        }
        if (needsForce) {
          setDialog({ ...dialog, force: true });
          return;
        }
        setSelected(new Set());
        setMenuId(null);
      }
      setDialog(null);
      await onReload();
    } catch (e) {
      setActionError(String((e as Error).message || e));
      throw e;
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const searchMode = query.trim().length >= 2;
  const isEmpty = !searchMode && !childFolders.length && !docsHere.length && !uploadingHere.length;
  const noSearchResults =
    searchMode &&
    !searching &&
    !crossFolderHits?.length &&
    !docsHere.length &&
    !uploadingHere.length;

  return (
    <section
      className={`file-browser${dragOver ? " is-dragover" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      onKeyDown={onBrowserKeyDown}
    >
      <div className="file-browser__toolbar row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <nav className="breadcrumbs" aria-label="Folder path">
          {breadcrumbs.map((c, i) => (
            <span key={`${c.id}-${i}`}>
              {i > 0 ? <span className="muted"> / </span> : null}
              {i === breadcrumbs.length - 1 ? (
                <span>{c.label}</span>
              ) : (
                <button type="button" className="ghost" onClick={() => onFolderChange(c.id)}>
                  {c.label}
                </button>
              )}
            </span>
          ))}
        </nav>
        <div className="row toolbar" style={{ gap: 8 }}>
          <label className="search-field">
            <IconSearch className="search-field__icon" />
            <input
              className="input"
              placeholder="Search files"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search files"
            />
          </label>
          <button type="button" className="ghost" disabled={busy} onClick={openCreateFolder}>
            New folder
          </button>
          <div className="upload-menu" ref={uploadMenuRef}>
            <button
              type="button"
              className="btn-with-icon upload-menu__trigger"
              aria-expanded={uploadMenuOpen}
              aria-haspopup="menu"
              disabled={busy}
              onClick={() => setUploadMenuOpen((open) => !open)}
            >
              <IconUpload aria-hidden />
              Add content
              <span className="upload-menu__chevron" aria-hidden>
                ▾
              </span>
            </button>
            {uploadMenuOpen ? (
              <div className="upload-menu__panel" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="upload-menu__item"
                  onClick={() => {
                    setUploadMenuOpen(false);
                    fileInputRef.current?.click();
                  }}
                >
                  <IconUpload aria-hidden />
                  <span>
                    <strong>Files</strong>
                    <small>Upload documents to {folderLabel}</small>
                  </span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="upload-menu__item"
                  onClick={() => {
                    setUploadMenuOpen(false);
                    folderInputRef.current?.click();
                  }}
                >
                  <IconFolder aria-hidden />
                  <span>
                    <strong>Folder</strong>
                    <small>Keep subfolders and upload in bulk</small>
                  </span>
                </button>
              </div>
            ) : null}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            accept={UPLOAD_ACCEPT}
            onChange={(e) => {
              startUpload(e.target.files);
              e.target.value = "";
            }}
          />
          <input
            ref={folderInputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              void startFolderUpload(e.target.files);
              e.target.value = "";
            }}
          />
        </div>
      </div>

      <p className="file-browser__hint">
        Drop files or folders here · uploading to <strong>{folderLabel}</strong>
      </p>
      {uploadNotice ? <p className="file-browser__hint muted">{uploadNotice}</p> : null}

      {!searchMode && !isEmpty ? (
        <div
          className={`file-bulk-bar${selectionCount > 0 ? " file-bulk-bar--active" : ""}`}
          role="toolbar"
          aria-label="Bulk actions"
        >
          <label className="file-row__check file-bulk-bar__select-all" onClick={(e) => e.stopPropagation()}>
            <input
              ref={selectAllRef}
              type="checkbox"
              checked={allSelected}
              disabled={selectableKeys.length === 0}
              onChange={(e) => setSelected((prev) => selectAllKeys(prev, selectableKeys, e.target.checked))}
              aria-label="Select all in this folder"
            />
          </label>
          {selectionCount > 0 ? (
            <>
              <button
                type="button"
                className="secondary btn-with-icon file-bulk-bar__btn"
                disabled={busy}
                onClick={openBulkMove}
              >
                <IconMove aria-hidden />
                Move
              </button>
              <button
                type="button"
                className="secondary btn-with-icon file-bulk-bar__btn"
                disabled={busy || readyDownloadCount === 0}
                onClick={() => void bulkDownload()}
              >
                <IconDownload aria-hidden />
                Download{readyDownloadCount > 0 ? ` (${readyDownloadCount})` : ""}
              </button>
              <button
                type="button"
                className="secondary danger btn-with-icon file-bulk-bar__btn"
                disabled={busy}
                onClick={openBulkDelete}
              >
                <IconTrash aria-hidden />
                Delete
              </button>
            </>
          ) : null}
          <span className={`file-bulk-bar__count${selectionCount === 0 ? " file-bulk-bar__count--idle" : ""}`}>
            {selectionCount} selected
          </span>
          {selectionCount > 0 ? (
            <button
              type="button"
              className="ghost file-bulk-bar__clear"
              onClick={() => setSelected(new Set())}
            >
              Clear
            </button>
          ) : null}
        </div>
      ) : null}

      {isEmpty ? (
        <div className="stack">
          {folderId ? (
            <button type="button" className="ghost" onClick={() => onFolderChange(currentFolder?.parent_id ?? null)}>
              ← Parent folder
            </button>
          ) : null}
          <EmptyState
            headline={folderId ? "This folder is empty" : "Add your first documents"}
            body="Drop PDFs, Word, or Excel here, or use Add files. Preview in-browser, then share a secure link."
            primaryLabel="Add files"
            onPrimary={() => fileInputRef.current?.click()}
            secondaryLabel="New folder"
            onSecondary={openCreateFolder}
          />
        </div>
      ) : (
        <div className="file-browser__body">
          {noSearchResults ? (
            <p className="muted file-browser__empty-search">No files match &ldquo;{query.trim()}&rdquo;</p>
          ) : null}
          {searchMode ? (
            <p className="muted file-browser__search-meta">
              {searching ? "Searching Space…" : `${crossFolderHits?.length ?? 0} result${(crossFolderHits?.length ?? 0) === 1 ? "" : "s"} across folders`}
            </p>
          ) : null}
          <ul className={`file-list${!searchMode && selectableKeys.length > 0 ? " file-list--selectable" : ""}`} role="list">
            {!searchMode && folderId && (
              <li className={selectableKeys.length > 0 ? "file-row-wrap file-row-wrap--selectable" : undefined}>
                {selectableKeys.length > 0 ? <span className="file-row__check-spacer" aria-hidden /> : null}
                <button
                  type="button"
                  className="file-row file-row--folder"
                  onClick={() => onFolderChange(currentFolder?.parent_id ?? null)}
                >
                  <span className="file-row__glyph">
                    <IconChevronUp />
                  </span>
                  <span className="file-row__name">..</span>
                  <span className="file-row__meta">Parent</span>
                </button>
              </li>
            )}
            {!searchMode &&
            childFolders.map((f) => {
              const key = folderKey(f.id);
              const picked = selected.has(key);
              return (
              <li key={f.id} className={`file-row-wrap file-row-wrap--selectable${picked ? " is-picked" : ""}`}>
                <FileRowCheckbox
                  checked={picked}
                  label={`Select folder ${f.name}`}
                  onChange={(on) => setSelected((prev) => toggleKey(prev, key, on))}
                />
                <button type="button" className="file-row file-row--folder" onClick={() => onFolderChange(f.id)}>
                  <span className="file-row__glyph">
                    <IconFolder />
                  </span>
                  <span className="file-row__name">
                    {f.name}
                    {f.stage_gate ? <span className="badge warn"> {f.stage_gate}</span> : null}
                  </span>
                  <span className="file-row__meta">Folder</span>
                </button>
                <div className="file-row__actions">
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => {
                      setDraft(f.name);
                      openDialog({ kind: "folder-rename", folder: f });
                    }}
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => openDialog({ kind: "confirm-delete-folder", folder: f })}
                  >
                    Delete
                  </button>
                </div>
              </li>
            );
            })}
            {uploadingHere.map((j) => (
              <li key={j.id} className={selectableKeys.length > 0 && !searchMode ? "file-row-wrap file-row-wrap--selectable" : undefined}>
                {selectableKeys.length > 0 && !searchMode ? <span className="file-row__check-spacer" aria-hidden /> : null}
                <div className="file-row file-row--uploading">
                <span className="file-row__glyph">
                  <IconUpload />
                </span>
                <span className="file-row__name">{j.name}</span>
                <span className="file-row__meta">
                  {j.status} {j.progress}%
                </span>
              </div>
              </li>
            ))}
            {searchMode
              ? (crossFolderHits ?? []).map(({ doc, hit }) => (
                  <li key={doc.id} className="file-row-wrap">
                    <div className="file-row-split">
                      <button
                        type="button"
                        className="file-row file-row--doc"
                        disabled={doc.status !== "ready"}
                        onClick={() => doc.status === "ready" && openAdminPreview(doc)}
                      >
                        <span className="file-row__glyph">
                          <FileTypeIcon name={doc.filename} />
                        </span>
                        <span className="file-row__name stack" style={{ gap: 2, alignItems: "flex-start" }}>
                          <span>{hit.title}</span>
                          {hit.folderPath ? <span className="muted" style={{ fontSize: 12 }}>{hit.folderPath}</span> : null}
                          {hit.snippet ? <span className="search-snippet">{hit.snippet}</span> : null}
                          {hit.matchType === "content" ? <span className="badge search-match-badge">In document</span> : null}
                        </span>
                        <span className="file-row__meta">{statusLabel(doc.status, doc.filename, doc.mime_type)}</span>
                      </button>
                    </div>
                  </li>
                ))
              : null}
            {!searchMode &&
            docsHere.map((d) => {
              const key = docKey(d.id);
              const picked = selected.has(key);
              return (
              <li key={d.id} className={`file-row-wrap file-row-wrap--selectable${menuId === d.id ? " is-open" : ""}${picked ? " is-picked" : ""}`} data-file-menu={d.id}>
                <FileRowCheckbox
                  checked={picked}
                  label={`Select ${d.display_name || d.filename}`}
                  onChange={(on) => setSelected((prev) => toggleKey(prev, key, on))}
                />
                <div className="file-row-split">
                  <button
                    type="button"
                    className="file-row file-row--doc"
                    disabled={d.status !== "ready"}
                    onClick={() => d.status === "ready" && openAdminPreview(d)}
                  >
                    <span className="file-row__glyph">
                      <FileTypeIcon name={d.filename} />
                    </span>
                    <span className="file-row__name">{d.display_name || d.filename}</span>
                    <span className="file-row__meta">
                      {d.status === "ready" ? (
                        <span className="badge ok">Ready</span>
                      ) : d.status === "processing" && isOfficeFile(d.filename, d.mime_type) ? (
                        <span className="badge">Converting</span>
                      ) : (
                        statusLabel(d.status, d.filename, d.mime_type)
                      )}
                      {d.page_count ? ` · ${d.page_count}p` : ""}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="ghost file-row__more"
                    aria-label={`Actions for ${d.filename}`}
                    aria-expanded={menuId === d.id}
                    aria-haspopup="menu"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuId(menuId === d.id ? null : d.id);
                    }}
                  >
                    ⋯
                  </button>
                </div>
                {menuId === d.id && (
                  <div className="file-menu" role="menu" onMouseDown={(e) => e.stopPropagation()}>
                    {d.status === "ready" ? (
                      <button type="button" className="ghost" onClick={() => openAdminPreview(d)}>
                        Preview
                      </button>
                    ) : null}
                    {d.status === "ready" ? (
                      <button
                        type="button"
                        className="ghost"
                        onClick={() =>
                          downloadDocument(`/api/v1/files/${d.id}/download`, d.filename, { admin: true }).catch(console.error)
                        }
                      >
                        Download
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="ghost"
                      onClick={() => {
                        setDraft(d.display_name || d.filename);
                        openDialog({ kind: "doc-rename", doc: d });
                      }}
                    >
                      Rename
                    </button>
                    <button
                      type="button"
                      className="ghost"
                      onClick={() => {
                        setDraft("");
                        openDialog({ kind: "doc-move", doc: d });
                      }}
                    >
                      Move…
                    </button>
                    <button
                      type="button"
                      className="ghost"
                      onClick={() => openDialog({ kind: "confirm-delete-doc", doc: d })}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </li>
            );
            })}
          </ul>
        </div>
      )}


      {dialog?.kind === "folder-create" && (
        <Modal
          title="New folder"
          onClose={() => setDialog(null)}
          primaryLabel={busy ? "Creating…" : "Create"}
          primaryDisabled={busy || !draft.trim()}
          onPrimary={() => submitDialog().catch(console.error)}
        >
          <label className="stack">
            Name
            <input
              className="input"
              value={draft}
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                if (busyRef.current) return;
                void submitDialog().catch(console.error);
              }}
            />
          </label>
        </Modal>
      )}
      {dialog?.kind === "folder-rename" && (
        <Modal
          title="Rename folder"
          onClose={() => setDialog(null)}
          primaryLabel={busy ? "Saving…" : "Save"}
          primaryDisabled={busy || !draft.trim()}
          onPrimary={() => submitDialog().catch(console.error)}
        >
          <label className="stack">
            Name
            <input className="input" value={draft} autoFocus onChange={(e) => setDraft(e.target.value)} />
          </label>
        </Modal>
      )}
      {dialog?.kind === "doc-rename" && (
        <Modal
          title="Rename file"
          onClose={() => setDialog(null)}
          primaryLabel={busy ? "Saving…" : "Save"}
          primaryDisabled={busy || !draft.trim()}
          onPrimary={() => submitDialog().catch(console.error)}
        >
          <label className="stack">
            Display name
            <input className="input" value={draft} autoFocus onChange={(e) => setDraft(e.target.value)} />
          </label>
        </Modal>
      )}
      {dialog?.kind === "doc-move" && (
        <Modal
          title={`Move “${dialog.doc.filename}”`}
          onClose={() => setDialog(null)}
          primaryLabel={busy ? "Moving…" : "Move"}
          primaryDisabled={busy}
          onPrimary={() => submitDialog().catch(console.error)}
        >
          <label className="stack">
            Destination
            <select className="input" value={draft} onChange={(e) => setDraft(e.target.value)}>
              <option value="">(Space root)</option>
              {folders.map((f) => (
                <option key={f.id} value={f.path}>
                  {f.path}
                </option>
              ))}
            </select>
          </label>
          {moveError ? <p className="muted" role="alert" style={{ color: "#9f1d2e", margin: 0 }}>{moveError}</p> : null}
        </Modal>
      )}
      {dialog?.kind === "confirm-delete-folder" && (
        <Modal
          title={dialog.force ? "Folder is not empty" : "Delete folder"}
          onClose={() => setDialog(null)}
          primaryLabel={dialog.force ? "Delete anyway" : "Delete"}
          danger
          primaryDisabled={busy}
          onPrimary={() => submitDialog().catch(console.error)}
        >
          <p className="muted" style={{ margin: 0 }}>
            {dialog.force
              ? `Delete “${dialog.folder.name}” and every file inside it? This cannot be undone.`
              : `Delete folder “${dialog.folder.name}”?`}
          </p>
          {actionError ? <p className="muted" role="alert" style={{ color: "#9f1d2e", margin: 0 }}>{actionError}</p> : null}
        </Modal>
      )}
      {dialog?.kind === "confirm-delete-doc" && (
        <Modal
          title="Delete file"
          onClose={() => setDialog(null)}
          primaryLabel="Delete"
          danger
          primaryDisabled={busy}
          onPrimary={() => submitDialog().catch(console.error)}
        >
          <p className="muted" style={{ margin: 0 }}>
            Delete “{dialog.doc.filename}”? This cannot be undone.
          </p>
          {actionError ? <p className="muted" role="alert" style={{ color: "#9f1d2e", margin: 0 }}>{actionError}</p> : null}
        </Modal>
      )}
      {dialog?.kind === "bulk-move" && (
        <Modal
          title={`Move ${dialog.docIds.length + dialog.folderIds.length} items`}
          onClose={() => setDialog(null)}
          primaryLabel={busy ? "Moving…" : "Move"}
          primaryDisabled={busy}
          onPrimary={() => submitDialog().catch(console.error)}
        >
          <p className="muted" style={{ margin: "0 0 12px" }}>
            {dialog.docIds.length ? `${dialog.docIds.length} file${dialog.docIds.length === 1 ? "" : "s"}` : null}
            {dialog.docIds.length && dialog.folderIds.length ? " and " : null}
            {dialog.folderIds.length ? `${dialog.folderIds.length} folder${dialog.folderIds.length === 1 ? "" : "s"}` : null}
          </p>
          <label className="stack">
            Destination
            <select className="input" value={draft} onChange={(e) => setDraft(e.target.value)}>
              <option value="">(Space root)</option>
              {folders.map((f) => (
                <option key={f.id} value={f.path}>
                  {f.path}
                </option>
              ))}
            </select>
          </label>
          {moveError ? <p className="muted" role="alert" style={{ color: "#9f1d2e", margin: 0 }}>{moveError}</p> : null}
        </Modal>
      )}
      {dialog?.kind === "bulk-delete" && (
        <Modal
          title={dialog.force ? "Some folders are not empty" : `Delete ${dialog.docIds.length + dialog.folderIds.length} items`}
          onClose={() => setDialog(null)}
          primaryLabel={dialog.force ? "Delete anyway" : "Delete"}
          danger
          primaryDisabled={busy}
          onPrimary={() => submitDialog().catch(console.error)}
        >
          <p className="muted" style={{ margin: 0 }}>
            {dialog.force
              ? "Selected folders contain files or subfolders. Delete them and every file inside? This cannot be undone."
              : `Delete ${dialog.docIds.length + dialog.folderIds.length} selected item${dialog.docIds.length + dialog.folderIds.length === 1 ? "" : "s"}? This cannot be undone.`}
          </p>
          {actionError ? <p className="muted" role="alert" style={{ color: "#9f1d2e", margin: 0 }}>{actionError}</p> : null}
        </Modal>
      )}
    </section>
  );
}
